/**
 * 1104 — a time chosen, never typed: three drop-downs (hour, minutes in 5-minute steps, AM or PM), each a native
 * select so a phone shows its scroll wheel and a screen reader reads it as a list. Eli, 4 Nov 2026: "instead of a
 * typing box, a drop down or scroll box where they can just select the hour, minutes, and AM, PM."
 * The value is "H:MM" in 24 hours when all three are chosen, '' otherwise.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const STEP_MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

interface Parts {
  h: string;
  m: string;
  ap: '' | 'am' | 'pm';
}
const fromValue = (v: string): Parts => {
  const x = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (!x) return { h: '', m: '', ap: '' };
  const h24 = Number(x[1]);
  return { h: String(((h24 + 11) % 12) + 1), m: String(Number(x[2])), ap: h24 < 12 ? 'am' : 'pm' };
};
const toValue = (p: Parts): string => {
  if (!p.h || p.m === '' || !p.ap) return '';
  const h12 = Number(p.h) % 12;
  return `${h12 + (p.ap === 'pm' ? 12 : 0)}:${String(Number(p.m)).padStart(2, '0')}`;
};

export function TimeSelect({ id, legend, value, onChange, required, invalid, describedBy, testId }: { id: string; legend: string; value: string; onChange: (v: string) => void; required?: boolean; invalid?: boolean; describedBy?: string; testId?: string }) {
  const { t } = useTranslation();
  const [p, setP] = useState<Parts>(() => fromValue(value));
  // a value given from outside (the scheduled time) is shown; a half-chosen time stays as the person left it
  useEffect(() => {
    if (value && value !== toValue(p)) setP(fromValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const change = (patch: Partial<Parts>) => {
    const next = { ...p, ...patch };
    setP(next);
    onChange(toValue(next));
  };
  const minutes = p.m !== '' && !STEP_MINUTES.includes(Number(p.m)) ? [...STEP_MINUTES, Number(p.m)].sort((a, b) => a - b) : STEP_MINUTES;
  const common = { className: 'input time-part', style: { width: '100%', minHeight: 44 }, 'aria-invalid': invalid || undefined, 'aria-required': required || undefined, 'aria-describedby': describedBy };
  return (
    <fieldset className="field time-select" data-testid={testId}>
      <legend>
        {legend}
        {required ? (
          <span className="req">
            {' '}
            <span aria-hidden="true">*</span>
            <span className="sr-only">{t('people.required')}</span>
          </span>
        ) : null}
      </legend>
      <div className="time-select-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <span className="time-part-wrap" style={{ flex: "1 1 6rem", minWidth: "5.5rem", display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor={`${id}-h`}>{t('vol.done.hour')}</label>
          <select id={`${id}-h`} {...common} value={p.h} onChange={(e) => change({ h: e.target.value })} data-testid={testId ? `${testId}-h` : undefined}>
            <option value="">—</option>
            {HOURS.map((h) => (
              <option key={h} value={String(h)}>
                {h}
              </option>
            ))}
          </select>
        </span>
        <span className="time-part-wrap" style={{ flex: "1 1 6rem", minWidth: "5.5rem", display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor={`${id}-m`}>{t('vol.done.minutes')}</label>
          <select id={`${id}-m`} {...common} value={p.m} onChange={(e) => change({ m: e.target.value })} data-testid={testId ? `${testId}-m` : undefined}>
            <option value="">—</option>
            {minutes.map((m) => (
              <option key={m} value={String(m)}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
        </span>
        <span className="time-part-wrap" style={{ flex: "1 1 6rem", minWidth: "5.5rem", display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor={`${id}-ap`}>{t('vol.done.ampm')}</label>
          <select id={`${id}-ap`} {...common} value={p.ap} onChange={(e) => change({ ap: e.target.value as Parts['ap'] })} data-testid={testId ? `${testId}-ap` : undefined}>
            <option value="">—</option>
            <option value="am">{t('vol.done.am')}</option>
            <option value="pm">{t('vol.done.pm')}</option>
          </select>
        </span>
      </div>
    </fieldset>
  );
}

/** "H:MM" (24 hours) from minutes after midnight. */
export const valueOfMinutes = (min: number): string => `${Math.floor(min / 60) % 24}:${String(min % 60).padStart(2, '0')}`;
